import type { DataScope } from 'serviceWorker/helpers/dataScope';
import { ConfigData } from 'serviceWorker/db/config';

/** What a request of the worker needs from the config; its version is the worker's own, not the stored one. */
export type HeadersData = Omit<ConfigData, '_id' | 'timezone' | 'organizationId' | 'frontendVersion'> & {
  _id?: number;
};
export interface ServiceDataShape {
  total: number;
  isDownloading: boolean;
  isDownloaded: boolean;
  projectId?: string;
  syncKey?: string;
  hasMore?: boolean;
}
export type InitAll = () => void;

export function hasRequiredConfig(
  setup: ConfigData | undefined,
  required: (keyof ConfigData)[]
): boolean {
  if (!setup) return false;
  return required.every((key) => Boolean(setup[key]));
}

export function createFinishedServiceData(
  total: number,
  extra?: Partial<ServiceDataShape>
): ServiceDataShape {
  return {
    total,
    isDownloading: false,
    isDownloaded: true,
    ...extra,
  };
}

export interface Data {
  locale: string;
  projectId: string;
  api: string;
}

/**
 * A download that failed and was handled by the pull itself (logged, its data
 * reset, `pullFailed` told): the pull resolves with it, so the pull handler
 * waits before it is asked again, without the callers of a sync getting an error.
 */
export interface FailedPull {
  error: unknown;
}

export type Pull = (
  data: Data,
  abortController: AbortController,
  /** The scope the caller read `data` in; a pull started on its own captures it at its start. */
  scope?: DataScope
) => void | FailedPull | Promise<void | FailedPull>;

export interface ResponseWithSyncKey<T = unknown> {
  entitiesWithoutAccess: string[];
  items: T[];
  syncKey: string;
}

export type NormalizedEntityResponseData<T = unknown> =
  | ResponseWithSyncKey<T>
  // undefined is when a request was made before required data was recieved (projectId)
  | undefined;

export type NormalizedPagedEntityResponseData<T = unknown> =
  | {
      entitiesWithoutAccess: string[];
      items: T[];
      syncKey: string;
      hasMore: boolean;
      totalCount: number;
    }
  // undefined is when a request was made before required data was recieved (projectId)
  | undefined;
